import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import apiFetch from "../lib/api";
import Notification from "../components/Notification";
import OtpInput from "../components/OtpInput";

interface LoginData { email: string; otp: string }

const Login: React.FC = () => {
  const [formData, setFormData] = useState<LoginData>({ email: "", otp: "" });
  const [otpSent, setOtpSent] = useState(false);
  const [requestingOTP, setRequestingOTP] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState<{ kind: 'success' | 'error' | 'info'; message: string } | null>(null);
  const showNotice = (message: string, kind: 'success' | 'error' | 'info' = 'info') => setNotice({ message, kind });
  const [otpCooldown, setOtpCooldown] = useState(0);
    useEffect(() => {
      if (otpCooldown <= 0) return;
      const t = setTimeout(() => setOtpCooldown((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }, [otpCooldown]);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await apiFetch("/auth/login-otp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: formData.email, otp: formData.otp }),
      });

      const data = await response.json();

      if (data.success) {
        // Check if user is admin
        if (data.user.role !== "admin") {
          setError("Access denied. Admin privileges required.");
          showNotice("Access denied. Admin privileges required.", 'error');
          setLoading(false);
          return;
        }

        // Use context login method
        login(data.user, data.token);

        // Navigate to dashboard
        navigate("/");
      } else {
        const msg = data.error || "Login failed";
        setError(msg);
        showNotice(msg, 'error');
      }
    } catch (err) {
      setError("Network error. Please try again.");
      showNotice("Network error. Please try again.", 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOTP = async () => {
    if (!formData.email) {
      setError("Please enter your email first.");
      showNotice("Please enter your email first.", 'error');
      return;
    }
    setRequestingOTP(true);
    setError("");
    try {
      const res = await apiFetch("/auth/otp/request-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email }),
      });
      const json = await res.json();
      if (json.success) {
        setOtpSent(true);
        setOtpCooldown(60);
        showNotice("OTP sent successfully.", 'success');
      } else {
        const msg = json.error || "Failed to send OTP";
        setError(msg);
        showNotice(msg, 'error');
      }
    } catch (e) {
      setError("Network error. Please try again.");
      showNotice("Network error. Please try again.", 'error');
    } finally {
      setRequestingOTP(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f9fafb",
      }}
    >
      {notice && (
        <Notification
          kind={notice.kind}
          message={notice.message}
          onClose={() => setNotice(null)}
        />
      )}
      <Card sx={{ maxWidth: 400, width: "100%", mx: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Admin Login
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to access the admin dashboard
            </Typography>
          </Box>


          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              required
              autoComplete="email"
              autoFocus
            />
            {otpSent && (
              <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                <OtpInput
                  value={formData.otp}
                  onChange={(otp) => setFormData((s) => ({ ...s, otp }))}
                  length={6}
                  disabled={loading}
                />
              </Box>
            )}
            <Button
              type="button"
              fullWidth
              variant="outlined"
              sx={{ mt: 2 }}
              disabled={requestingOTP || !formData.email || (otpSent && otpCooldown > 0)}
              onClick={handleRequestOTP}
            >
              {requestingOTP
                ? <CircularProgress size={24} />
                : otpSent
                  ? (otpCooldown > 0 ? `Resend OTP (${otpCooldown}s)` : "Resend OTP")
                  : "Send OTP"}
            </Button>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || !otpSent}
            >
              {loading ? <CircularProgress size={24} /> : "Sign In with OTP"}
            </Button>
          </form>

        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
